// Admin console: Basis Pengetahuan (docs/contract.md 4, 9.2). Regions, each
// kept by design review ui.region_gate:
//   header        plain spacing, hairline below  h1, count, Tambah berkas, Muat ulang
//   errors        notice                          files that failed to load, in Indonesian, with Perbaiki
//   system_files  bento                           Katalog dokumen and Pertanyaan umum: count, updated, Lihat, Ganti isi
//                                                 (divided_section was the primary at 0.19 confidence and repeats the
//                                                 toolbar's container, so the runner-up applies)
//   toolbar       divided section                 search and Kategori
//   files         rows                            DataTable from 1024 (Lihat, Ganti isi, Hapus), link rows below
// The format help lives on the editor (design review help_placement editor_aside).
import { useMemo, useState } from "react";
import * as API from "../api";
import { formatCount, formatDate, formatDateTime } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL, TAHAP_LABEL, TAHAP_ORDER } from "../lib/labels";
import { Dialog, EmptyState, Notice, PageHeader, SearchInput, SectionHead, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { fileNameError, formatSize, SYSTEM_FILES, SYSTEM_ORDER } from "./knowledge-lib";
import { DriveKnowledgeSection } from "./KnowledgeDrive";

const CATEGORY_ORDER = ["regulasi", "template", "panduan", "contoh"];

export function tahapText(tahap: string[] | undefined): string {
  if (!tahap || tahap.length === 0) return "Semua tahap";
  return tahap.map((t) => ((TAHAP_ORDER as string[]).includes(t) ? TAHAP_LABEL[t as API.Tahap] : t)).join(", ");
}

export function fileHref(file: string, action?: "ganti"): string {
  const base = `/admin/pengetahuan/${encodeURIComponent(file)}`;
  return action ? `${base}/${action}` : base;
}

function sortFiles(files: API.AdminKnowledgeFile[]): API.AdminKnowledgeFile[] {
  return [...files].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a.category);
    const ib = CATEGORY_ORDER.indexOf(b.category);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.title.localeCompare(b.title, "id");
  });
}

/** Generated from the Google Drive upload folder (managed in Drive, not here). */
export function isDriveFile(file: string): boolean {
  return file.startsWith("drive/");
}

export function KnowledgeAdminScreen() {
  useTitle("Basis Pengetahuan");
  const kb = useAsync(() => API.getAdminKnowledge(), []);
  const [reload, setReload] = useState<{ busy: boolean; error: string | null; done: boolean }>({ busy: false, error: null, done: false });
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [deleting, setDeleting] = useState<API.AdminKnowledgeFile | null>(null);
  const [deleted, setDeleted] = useState<string | null>(null);

  async function doReload() {
    setReload({ busy: true, error: null, done: false });
    setDeleted(null);
    try {
      kb.set(await API.reloadAdminKnowledge());
      setReload({ busy: false, error: null, done: true });
    } catch (err) {
      setReload({ busy: false, error: API.errorMessage(err), done: false });
    }
  }

  // Files generated from Google Drive (drive/...) are listed in their own section below.
  const all = useMemo(() => sortFiles((kb.data?.files ?? []).filter((f) => !isDriveFile(f.file))), [kb.data]);
  const s = q.trim().toLowerCase();
  const shown = all.filter((f) => (!category || f.category === category) && (!s || f.title.toLowerCase().includes(s) || f.file.toLowerCase().includes(s)));
  const categories = [...new Set(all.map((f) => f.category))].sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b));
  const errors = kb.data?.errors ?? [];
  const system = SYSTEM_ORDER.map((file) => ({ file, meta: SYSTEM_FILES[file]!, info: kb.data?.system?.find((x) => x.file === file) ?? null }));
  const examples = all.filter((f) => f.file.startsWith("contoh-")).length;

  return (
    <>
      <PageHeader
        divided
        title="Basis Pengetahuan"
        lead={kb.data ? `${formatCount(all.length)} berkas dimuat. Pluto hanya merujuk isi dari berkas ini.` : "Memuat berkas pengetahuan"}
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={doReload} aria-busy={reload.busy || undefined}>
              <Glyph name="refresh" />
              <span>Muat ulang</span>
            </button>
            <a className="btn" href="/admin/pengetahuan/baru">
              <Glyph name="add" />
              <span>Tambah berkas</span>
            </a>
          </>
        }
      />

      {reload.error ? (
        <Notice tone="danger" title="Basis pengetahuan belum dimuat ulang">
          <p>{reload.error}</p>
        </Notice>
      ) : reload.done ? (
        <Notice tone="success" title="Basis pengetahuan dimuat ulang">
          <p>Daftar di bawah sudah memakai isi berkas terbaru.</p>
        </Notice>
      ) : deleted ? (
        <Notice tone="success" title={`Berkas ${deleted} dihapus`}>
          <p>Pluto tidak lagi merujuk isi berkas ini.</p>
        </Notice>
      ) : null}

      {errors.length > 0 ? (
        <Notice tone="danger" title={`${formatCount(errors.length)} berkas gagal dimuat`}>
          <p>Berkas ini dilewati dan tidak dipakai Pluto sampai isinya diperbaiki. Versi valid terakhir tetap dipakai bila ada.</p>
          <ul className="error-list kb-error-list">
            {errors.map((e) => (
              <li key={e.file}>
                <span className="kb-error-text">
                  <span className="mono">{e.file}</span>
                  <span>{e.message}</span>
                </span>
                {fileNameError(e.file) === null ? (
                  <a className="btn btn-secondary" href={fileHref(e.file, "ganti")} aria-label={`Perbaiki ${e.file}`}>
                    Perbaiki
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <section className="region" aria-labelledby="kb-system-title">
        <SectionHead id="kb-system-title" title="Berkas sistem" lead="Mengatur katalog Buat Dokumen, pertanyaan cepat Pluto, dan video E-Learning. Dapat diganti isinya, tidak dapat dihapus." />
        <ul className="system-grid">
          {system.map(({ file, meta, info }) => (
            <li key={file} className="card system-tile">
              <div className="system-tile-text">
                <h3 className="card-subtitle">{meta.label}</h3>
                <p className="system-tile-lead">{meta.lead}</p>
              </div>
              <dl className="system-facts">
                <div className="fact">
                  <dt>Berkas</dt>
                  <dd className="mono">{file}</dd>
                </div>
                <div className="fact">
                  <dt>Isi</dt>
                  <dd>{info ? `${formatCount(info.itemCount)} ${meta.unit}` : kb.data ? "Belum dimuat" : "Memuat"}</dd>
                </div>
                <div className="fact">
                  <dt>Diperbarui</dt>
                  <dd>{info ? formatDateTime(info.updatedAt) : "Tidak diketahui"}</dd>
                </div>
              </dl>
              <div className="button-row system-tile-actions">
                {meta.manage ? (
                  <a className="btn" href={meta.manage.href}>
                    <Glyph name="book" />
                    <span>{meta.manage.label}</span>
                  </a>
                ) : null}
                <a className="btn btn-secondary" href={fileHref(file)} aria-label={`Lihat isi ${meta.label}`}>
                  <Glyph name="eye" />
                  <span>Lihat isi</span>
                </a>
                <a className="btn btn-secondary" href={fileHref(file, "ganti")} aria-label={`Ganti isi ${meta.label}`}>
                  <Glyph name="edit2" />
                  <span>Ganti isi</span>
                </a>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <DriveKnowledgeSection />

      <section className="region" aria-labelledby="kb-files-title">
        <SectionHead
          id="kb-files-title"
          title="Berkas pengetahuan"
          lead={
            examples > 0
              ? "Berkas berawalan contoh- adalah contoh bawaan. Ganti atau hapus setelah dokumen resmi tersedia."
              : "Satu berkas untuk satu sumber: peraturan, templat, panduan, atau contoh dokumen."
          }
        />
        <div className="toolbar kb-toolbar">
          <div className="field-row" data-cols="3" role="search">
            <div className="field">
              <label htmlFor="kb-q">Cari berkas</label>
              <SearchInput id="kb-q" value={q} onChange={setQ} placeholder="Judul atau nama berkas" />
            </div>
            <div className="field">
              <label htmlFor="kb-cat">Kategori</label>
              <select id="kb-cat" value={category} onChange={(e) => setCategory(e.currentTarget.value)}>
                <option value="">Semua kategori</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {KNOWLEDGE_CATEGORY_LABEL[c] ?? c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {kb.loading && !kb.data ? (
          <SkeletonRows count={6} label="Memuat berkas" />
        ) : kb.error ? (
          <Notice tone="danger" title="Basis pengetahuan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={kb.reload}>Muat ulang</button>}>
            <p>{API.errorMessage(kb.error)}</p>
          </Notice>
        ) : all.length === 0 ? (
          <EmptyState
            icon="bookOpen"
            title="Belum ada berkas pengetahuan"
            body="Unggah berkas .md pertama, misalnya satu peraturan atau satu templat."
            action={
              <a className="btn" href="/admin/pengetahuan/baru">
                Tambah berkas
              </a>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState icon="filter" title="Tidak ada berkas yang cocok" body="Ubah kata kunci atau pilih semua kategori." />
        ) : (
          <>
            <div className="records-table" data-from="1280">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Judul</th>
                    <th scope="col">Kategori</th>
                    <th scope="col">Tahap</th>
                    <th scope="col" className="num-col">
                      Bagian
                    </th>
                    <th scope="col" className="num-col">
                      Ukuran
                    </th>
                    <th scope="col">Diperbarui</th>
                    <th scope="col" className="action-col">
                      Tindakan
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => (
                    <tr key={f.file}>
                      <td className="kb-title-cell">
                        <a className="cell-title cell-link" href={fileHref(f.file)}>
                          {f.title}
                        </a>
                        <span className="cell-meta mono">{f.file}</span>
                      </td>
                      <td className="nowrap">{KNOWLEDGE_CATEGORY_LABEL[f.category] ?? f.category}</td>
                      <td className="kb-tahap-cell">{tahapText(f.tahap)}</td>
                      <td className="num-col num">{formatCount(f.sectionCount)}</td>
                      <td className="num-col num">{formatSize(f.size)}</td>
                      <td className="num nowrap">{f.updatedAt ? formatDate(f.updatedAt) : "Tidak diketahui"}</td>
                      <td className="action-col">
                        <div className="row-actions">
                          <a className="ui-icon-btn" href={fileHref(f.file, "ganti")} aria-label={`Ganti isi ${f.title}`} title="Ganti isi">
                            <Glyph name="edit2" />
                          </a>
                          <button type="button" className="ui-icon-btn" aria-label={`Hapus ${f.title}`} title="Hapus" onClick={() => setDeleting(f)}>
                            <Glyph name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="rows records-list" data-from="1280">
              {shown.map((f) => (
                <li key={f.file}>
                  <a className="row row-link" href={fileHref(f.file)}>
                    <span className="row-main">
                      <span className="row-title">{f.title}</span>
                      <span className="row-meta mono">{f.file}</span>
                    </span>
                    <span className="row-side">
                      <span className="row-meta">
                        {KNOWLEDGE_CATEGORY_LABEL[f.category] ?? f.category}, <span className="num">{formatCount(f.sectionCount)}</span> bagian
                      </span>
                    </span>
                    <span className="row-chevron" aria-hidden="true">
                      <Glyph name="chevronRight" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {deleting ? (
        <DeleteFileDialog
          file={deleting.file}
          title={deleting.title}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setReload({ busy: false, error: null, done: false });
            setDeleted(deleting.file);
            if (kb.data) kb.set({ ...kb.data, files: kb.data.files.filter((x) => x.file !== deleting.file) });
          }}
        />
      ) : null}
    </>
  );
}

/** Confirms a delete by name. System files never reach it (the API answers 422 anyway). */
export function DeleteFileDialog({ file, title, onClose, onDeleted }: { file: string; title: string; onClose: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function remove() {
    setBusy(true);
    setFailure(null);
    try {
      await API.deleteKnowledgeFile(file);
      onDeleted();
    } catch (err) {
      setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      size="sm"
      busy={busy}
      title={`Hapus berkas ${file}?`}
      description={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="button" className="btn" data-tone="danger" onClick={remove} aria-busy={busy || undefined}>
            <Glyph name="trash" />
            <span>Hapus berkas</span>
          </button>
        </>
      }
    >
      <div className="confirm-body">
        {failure ? (
          <Notice tone="danger" title="Berkas belum dihapus">
            <p>{failure}</p>
          </Notice>
        ) : null}
        <p className="confirm-text">Berkas ini dihapus dari basis pengetahuan dan tidak lagi dirujuk Pluto. Dokumen yang sudah menyimpan rujukannya tidak berubah.</p>
      </div>
    </Dialog>
  );
}

